// Online-Marketing modal shell: SEO-Audit view lives here, Blog/Social/
// Einstellungen tabs are delegated to their own modules.
// Data contract: ONLINE_MARKETING_HANDOFF_FRANK.md §3.
import { initBlogTab } from "./marketing-blog.js";
import { initSocialTab } from "./marketing-social.js";
import { initSettingsTab } from "./marketing-settings.js";
import { initFilesTab } from "./marketing-files.js";
import { initRadarTab } from "./marketing-radar.js";
import { initAnalyticsTab } from "./marketing-analytics.js";
import { initBilanzTab } from "./marketing-bilanz.js";
import { initFollowersTab } from "./marketing-followers.js";
import { initInboxTab } from "./marketing-inbox.js";
import { initOpportunitiesTab } from "./marketing-opportunities.js";
import { initApiCostsTab } from "./marketing-apicosts.js";
import { initPerformanceTab } from "./marketing-performance.js";
import { initAdsTab } from "./marketing-ads.js";
import { initMetaTab } from "./marketing-meta.js";
import { initSistrixSeo } from "./marketing-sistrix.js";
import { initMasterplanTab } from "./marketing-masterplan.js";
import { initAwinTab } from "./marketing-awin.js";
import { initPartnerTab } from "./marketing-partner.js";
import { baueCockpitNavigation, COCKPIT_TITEL } from "./marketing-cockpit.js";

interface PortalSummary {
  label: string; articles: number; altGaps: number; brokenImg: number;
  metaEmpty: number; redTotal: number; redEmptyTarget: number; redSelf: number;
  redDup: number; homeStatus: string; ldOk: number; ldBad: number;
  sitemapStatus: string; sitemapUrls: number;
}
interface SeoSummary { portals: PortalSummary[]; issues: Array<{ portal: string; kind: string; count: number; text: string }> }
interface ReportsResponse {
  latest: { id: number; runStamp: string; createdAt: string; status: string; hasMarkdown: boolean; summary: SeoSummary } | null;
  history: Array<{ id: number; runStamp: string; createdAt: string; status: string }>;
}
interface JobsResponse { jobs: Array<{ id: number; type: string; status: string; started_at: string; finished_at: string | null; log: string | null }> }

export type ApiFn = <T>(url: string, method?: "GET" | "POST" | "PUT", body?: unknown) => Promise<{ status: number; data: T | null }>;

export interface MarketingDeps {
  api: ApiFn;
  openModal: (templateId: string) => HTMLElement;
  formatDate: (iso: string) => string;
}

const PORTAL_COLUMNS: Array<[keyof PortalSummary, string]> = [
  ["label", "Portal"], ["articles", "Artikel"], ["altGaps", "alt-Lücken"],
  ["brokenImg", "kaputte img"], ["metaEmpty", "Meta leer"], ["redTotal", "Redirects"],
  ["redEmptyTarget", "leeres Ziel"], ["redSelf", "Self"], ["redDup", "Dupes"],
  ["homeStatus", "Home"], ["ldOk", "LD ok"], ["ldBad", "LD bad"],
  ["sitemapStatus", "Sitemap"], ["sitemapUrls", "URLs"],
];

function renderSummary(host: HTMLElement, summary: SeoSummary): void {
  host.textContent = "";
  const table = document.createElement("table");
  table.className = "om-table";
  const headRow = table.createTHead().insertRow();
  for (const [, label] of PORTAL_COLUMNS) {
    const th = document.createElement("th");
    th.textContent = label;
    headRow.appendChild(th);
  }
  const tbody = table.createTBody();
  for (const p of summary.portals) {
    const tr = tbody.insertRow();
    for (const [key] of PORTAL_COLUMNS) {
      tr.insertCell().textContent = String(p[key] ?? "—");
    }
  }
  host.appendChild(table);

  if (summary.issues.length) {
    const h = document.createElement("h3");
    h.textContent = `Handlungsbedarf (${summary.issues.length})`;
    host.appendChild(h);
    const ul = document.createElement("ul");
    ul.className = "om-issues";
    for (const i of summary.issues) {
      const li = document.createElement("li");
      li.textContent = `${i.portal}: ${i.text}`;
      ul.appendChild(li);
    }
    host.appendChild(ul);
  } else {
    const p = document.createElement("p");
    p.textContent = "✅ Keine offenen Issues.";
    host.appendChild(p);
  }
}

interface TasksResponse { tabs: { inbox: number; social: number; ads?: number }; total: number }

function setTabBadge(btn: Element | null, n: number): void {
  if (!btn) return;
  let badge = btn.querySelector(".tab-badge") as HTMLElement | null;
  if (!badge) { badge = document.createElement("span"); badge.className = "tab-badge"; btn.appendChild(badge); }
  badge.textContent = String(n);
  badge.hidden = n === 0;
}

// Holt die offenen Aufgaben (Kommentare + unbelegte Slots) und setzt das Menü-Badge
// und – falls die Suite offen ist – die Zähler an den Reitern Inbox/Social.
export async function refreshMarketingTasks(deps: MarketingDeps, modal?: HTMLElement): Promise<void> {
  try {
    const r = await deps.api<TasksResponse>("/api/marketing/tasks");
    const t = r.data;
    const n = t?.total ?? 0;
    const mb = document.getElementById("marketing-badge");
    if (mb) { mb.textContent = String(n); mb.hidden = n === 0; }
    if (modal && t) {
      setTabBadge(modal.querySelector('[data-tab="inbox"]'), t.tabs.inbox);
      setTabBadge(modal.querySelector('[data-tab="social"]'), t.tabs.social);
      setTabBadge(modal.querySelector('[data-tab="ads"]'), t.tabs.ads ?? 0);
    }
  } catch { /* Badge ist nice-to-have, Fehler ignorieren */ }
}

export function setupMarketing(deps: MarketingDeps): void {
  const btn = document.getElementById("marketing-btn");
  if (!btn) return;
  // Menüknopf heißt „Cockpit" (Uli 12.10.2026); das Zähler-Span dahinter bleibt erhalten
  if (btn.firstChild?.nodeType === Node.TEXT_NODE) btn.firstChild.textContent = "🧭 Cockpit";
  btn.title = `${COCKPIT_TITEL}: Planung, Inhalte & Social, SEO & Analyse, Werbung & Umsatz, Verwaltung`;
  btn.addEventListener("click", () => { void openMarketingModal(deps); });
  void refreshMarketingTasks(deps);
}

async function openMarketingModal(deps: MarketingDeps): Promise<void> {
  const { api, openModal, formatDate } = deps;
  const m = openModal("tpl-marketing-modal");
  initRadarTab(m, deps);
  initAnalyticsTab(m, deps);
  initBilanzTab(m, deps);
  initFollowersTab(m, deps);
  initInboxTab(m, deps);
  initOpportunitiesTab(m, deps);
  initApiCostsTab(m, deps);
  initPerformanceTab(m, deps);
  initAdsTab(m, deps);
  initMetaTab(m, deps);
  initSistrixSeo(m, deps);
  initBlogTab(m, deps);
  initSocialTab(m, deps);
  initSettingsTab(m, deps);
  initFilesTab(m, deps);
  initMasterplanTab(m, deps);
  initAwinTab(m, deps);
  initPartnerTab(m, deps);
  void refreshMarketingTasks(deps, m);

  // Tab switching — same pattern as the config modal.
  m.querySelectorAll<HTMLButtonElement>(".tab-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      m.querySelectorAll(".tab-btn").forEach((b) => b.classList.remove("active"));
      m.querySelectorAll(".tab-panel").forEach((p) => p.classList.remove("active"));
      btn.classList.add("active");
      m.querySelector(`[data-tab-panel="${btn.dataset.tab}"]`)?.classList.add("active");
    });
  });
  // WIP-Cockpit (12.10.2026): Bereiche oben, Reiter des Bereichs darunter — NACH den Klick-Handlern,
  // weil die Bereichswahl den gemerkten Reiter per click() öffnet.
  baueCockpitNavigation(m);

  const summaryHost = m.querySelector("[data-seo-summary]") as HTMLElement;
  const historySel = m.querySelector("[data-history]") as HTMLSelectElement;
  const msg = m.querySelector('[data-msg="seo"]') as HTMLElement;
  let pollTimer: number | undefined;

  const loadReports = async (): Promise<void> => {
    const r = await api<ReportsResponse>("/api/marketing/seo-reports");
    const data = r.data;
    if (!data) return;
    historySel.textContent = "";
    for (const h of data.history) {
      const o = document.createElement("option");
      o.value = String(h.id);
      o.textContent = `${h.runStamp} (${h.status})`;
      historySel.appendChild(o);
    }
    if (data.latest) renderSummary(summaryHost, data.latest.summary);
  };

  m.querySelector('[data-action="open-report"]')?.addEventListener("click", () => {
    const id = historySel.value;
    if (id) window.open(`/api/marketing/seo-reports/${id}/markdown`, "_blank");
  });

  m.querySelector('[data-action="run-audit"]')?.addEventListener("click", () => {
    void (async () => {
      msg.textContent = "Starte Audit …";
      const r = await api<{ ok?: boolean; stamp?: string; error?: string }>("/api/marketing/seo-audit/run", "POST");
      if (r.status === 409) { msg.textContent = "Läuft bereits."; return; }
      if (r.status !== 200) { msg.textContent = `Fehler (${r.status})`; return; }
      msg.textContent = `Läuft (${r.data?.stamp}) — Ergebnis erscheint hier automatisch.`;
      // Poll until the motor's job row reports done/failed. The motor side
      // may not be wired to om_jobs yet — polling stops with the modal.
      const poll = async (): Promise<void> => {
        const jr = await api<JobsResponse>("/api/marketing/jobs?type=seo_audit");
        const finished = jr.data?.jobs.find((j) => j.status !== "running");
        const running = jr.data?.jobs.some((j) => j.status === "running");
        if (!running && finished) {
          msg.textContent = finished.status === "done"
            ? `Fertig: ${formatDate(finished.finished_at ?? finished.started_at)}`
            : `Fehlgeschlagen: ${finished.log ?? ""}`;
          await loadReports();
          return;
        }
        if (m.isConnected) pollTimer = window.setTimeout(() => { void poll(); }, 5000);
      };
      pollTimer = window.setTimeout(() => { void poll(); }, 5000);
    })();
  });

  m.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-backdrop")) window.clearTimeout(pollTimer);
  });

  await loadReports();
}
